/**
 * Secure storage utility for encrypting/decrypting sensitive data
 * Uses browser's Web Crypto API for client-side encryption
 */

class SecureStorage {
  algorithm: string;
  keyLength: number;
  ivLength: number;

  constructor() {
    this.algorithm = 'AES-GCM';
    this.keyLength = 256;
    this.ivLength = 12; // 96 bits for GCM
  }

  /**
   * Generate a cryptographic key for encryption/decryption
   * @returns {Promise<CryptoKey>}
   */
  async generateKey(): Promise<CryptoKey> {
    return await crypto.subtle.generateKey(
      {
        name: this.algorithm,
        length: this.keyLength,
      },
      true, // extractable
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Derive a key from user session or other entropy
   * @param {string} password - Base password/session identifier
   * @returns {Promise<CryptoKey>}
   */
  async deriveKey(password: string): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      { name: 'PBKDF2' },
      false,
      ['deriveKey']
    );

    // Use a fixed salt for consistent key derivation
    // In production, consider using a per-user salt
    const salt = encoder.encode('canvas-app-salt-2024');

    return await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 100000,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: this.algorithm, length: this.keyLength },
      false,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Encrypt data
   * @param {string} data - Data to encrypt
   * @param {CryptoKey} key - Encryption key
   * @returns {Promise<string>} Base64 encoded encrypted data with IV
   */
  async encrypt(data: string, key: CryptoKey): Promise<string> {
    try {
      const encoder = new TextEncoder();
      const iv = crypto.getRandomValues(new Uint8Array(this.ivLength));
      
      const encrypted = await crypto.subtle.encrypt(
        {
          name: this.algorithm,
          iv: iv,
        },
        key,
        encoder.encode(data)
      );

      // Combine IV and encrypted data
      const combined = new Uint8Array(iv.length + encrypted.byteLength);
      combined.set(iv);
      combined.set(new Uint8Array(encrypted), iv.length);

      // Convert to base64 for storage
      return btoa(String.fromCharCode(...combined));
    } catch (error) {
      console.error('Encryption failed:', error);
      throw new Error('Failed to encrypt data');
    }
  }

  /**
   * Decrypt data
   * @param {string} encryptedData - Base64 encoded encrypted data with IV
   * @param {CryptoKey} key - Decryption key
   * @returns {Promise<string>} Decrypted data
   */
  async decrypt(encryptedData: string, key: CryptoKey): Promise<string> {
    try {
      // Convert from base64
      const combined = new Uint8Array(
        atob(encryptedData)
          .split('')
          .map(char => char.charCodeAt(0))
      );

      // Extract IV and encrypted data
      const iv = combined.slice(0, this.ivLength);
      const encrypted = combined.slice(this.ivLength);

      const decrypted = await crypto.subtle.decrypt(
        {
          name: this.algorithm,
          iv: iv,
        },
        key,
        encrypted
      );

      const decoder = new TextDecoder();
      return decoder.decode(decrypted);
    } catch (error) {
      console.error('Decryption failed:', error);
      throw new Error('Failed to decrypt data');
    }
  }

  /**
   * Store encrypted data in localStorage
   * @param {string} key - Storage key
   * @param {any} data - Data to encrypt and store
   * @param {string} userSession - User session for key derivation
   */
  async setItem(key: string, data: any, userSession: string): Promise<void> {
    try {
      const encryptionKey = await this.deriveKey(userSession);

      // Handle JWT tokens (don't stringify them)
      let dataToEncrypt: string;
      if (typeof data === 'string' && (data.startsWith('eyJ') || key.includes('Token'))) {
        dataToEncrypt = data; // JWT tokens are already strings
      } else {
        dataToEncrypt = JSON.stringify(data);
      }
      
      const encrypted = await this.encrypt(dataToEncrypt, encryptionKey);
      localStorage.setItem(`secure_${key}`, encrypted);
    } catch (error) {
      console.error('Secure storage setItem failed:', error);
      // Fallback to regular storage with warning
      console.warn('Falling back to unencrypted storage');
      
      // Handle JWT tokens in fallback too
      if (typeof data === 'string' && (data.startsWith('eyJ') || key.includes('Token'))) {
        localStorage.setItem(key, data);
      } else {
        localStorage.setItem(key, JSON.stringify(data));
      }
    }
  }

  /**
   * Retrieve and decrypt data from localStorage
   * @param {string} key - Storage key
   * @param {string} userSession - User session for key derivation
   * @returns {Promise<any>} Decrypted data
   */
  async getItem(key: string, userSession: string): Promise<any> {
    try {
      const encrypted = localStorage.getItem(`secure_${key}`);
      if (!encrypted) {
        // Check for fallback unencrypted data
        const fallback = localStorage.getItem(key);
        if (!fallback) return null;
        
        // Check if it's a JWT token (starts with eyJ) - don't parse as JSON
        if (typeof fallback === 'string' && (fallback.startsWith('eyJ') || key.includes('Token'))) {
          return fallback;
        }
        
        try {
          return JSON.parse(fallback);
        } catch (parseError) {
          // If JSON parsing fails, return as string
          return fallback;
        }
      }

      // Try to decrypt the secure data
      try {
        const encryptionKey = await this.deriveKey(userSession);
        const decrypted = await this.decrypt(encrypted, encryptionKey);
        
        // Check if decrypted value is a JWT token - don't parse as JSON
        if (typeof decrypted === 'string' && (decrypted.startsWith('eyJ') || key.includes('Token'))) {
          return decrypted;
        }
        
        try {
          return JSON.parse(decrypted);
        } catch (parseError) {
          // If JSON parsing fails, return as string
          return decrypted;
        }
      } catch (decryptionError) {
        console.warn(`Decryption failed for key '${key}', falling back to unencrypted storage:`, decryptionError);
        
        // Clear the corrupted encrypted data
        localStorage.removeItem(`secure_${key}`);
        
        // Try fallback to unencrypted storage
        const fallback = localStorage.getItem(key);
        if (!fallback) return null;
        
        // Check if it's a JWT token - don't parse as JSON
        if (typeof fallback === 'string' && (fallback.startsWith('eyJ') || key.includes('Token'))) {
          return fallback;
        }
        
        try {
          return JSON.parse(fallback);
        } catch (parseError) {
          return fallback;
        }
      }
    } catch (error) {
      console.error('Secure storage getItem failed:', error);
      // Try fallback unencrypted storage
      try {
        const fallback = localStorage.getItem(key);
        if (!fallback) return null;
        
        // Check if it's a JWT token - don't parse as JSON
        if (typeof fallback === 'string' && (fallback.startsWith('eyJ') || key.includes('Token'))) {
          return fallback;
        }
        
        try {
          return JSON.parse(fallback);
        } catch (parseError) {
          return fallback;
        }
      } catch (fallbackError) {
        console.error('Fallback storage also failed:', fallbackError);
        return null;
      }
    }
  }

  /**
   * Remove encrypted data from localStorage
   * @param {string} key - Storage key
   */
  removeItem(key: string): void {
    localStorage.removeItem(`secure_${key}`);
    localStorage.removeItem(key); // Also remove any fallback
  }

  /**
   * Clear all secure storage
   */
  clear(): void {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('secure_')) {
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Clear corrupted secure storage and reset
   * This is useful when decryption consistently fails
   */
  clearCorruptedData(): void {
    console.warn('Clearing potentially corrupted secure storage data');
    this.clear();
    
    // Also clear auth-related localStorage items that might be causing issues
    const authKeys = ['authToken', 'idToken', 'refreshToken', 'tokenExpiry', 'userEmail'];
    authKeys.forEach(key => {
      const hasSecure = localStorage.getItem(`secure_${key}`);
      const hasRegular = localStorage.getItem(key);
      
      if (hasSecure && !hasRegular) {
        console.warn(`Found secure storage for ${key} but no fallback - this may be corrupted`);
        localStorage.removeItem(`secure_${key}`);
      }
    });
  }

  /**
   * Debug method to check storage state
   */
  debugStorage(): void {
    console.log('=== SECURE STORAGE DEBUG ===');
    const keys = Object.keys(localStorage);
    const secureKeys = keys.filter(key => key.startsWith('secure_'));
    const regularKeys = keys.filter(key => !key.startsWith('secure_'));
    
    console.log('Secure storage keys:', secureKeys);
    console.log('Regular storage keys:', regularKeys);
    
    secureKeys.forEach(key => {
      const regularKey = key.replace('secure_', '');
      const hasRegularVersion = localStorage.getItem(regularKey);
      console.log(`${key}: has regular fallback = ${!!hasRegularVersion}`);
    });
  }
}

// Create singleton instance
const secureStorage = new SecureStorage();

export default secureStorage;
